/**
 * CreateGroup Component
 * Clean version of the original 3,220-line CreateGroup component
 * Uses custom hooks and focused sub-components for better maintainability
 */

import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { useCreateGroupState } from '@/hooks/ea/useManageGroupState';
import { useManageGroup } from '@/hooks/ea/useManageGroup';
import { useGroupFiltering } from '@/hooks/ea/useGroupFiltering';
import { useEnrollmentSpecialists } from '@/hooks/ea/useEnrollmentSpecialists.ts';

// Import focused components
import { CreateGroupHeader } from '@/components/ea/CreateGroupHeader';
import { GroupFormSection } from '@/components/ea/GroupFormSection';
import { ClinicSelectionSection } from '@/components/ea/ClinicSelectionSection';
import { PatientTableSection } from '@/components/ea/PatientTableSection';
import { EMRInputSection } from '@/components/ea/EMRInputSection';
import { ActionButtonsSection } from '@/components/ea/ActionButtonsSection';
import { GroupFilteringComponent } from '@/components/ea/GroupFilteringComponent';
import LoaderComponent from '@/components/common/LoaderComponent';

interface CreateGroupProps {
  onBack: () => void;
  onSuccess?: () => void;
  duplicateGroupId?: number;
  editGroupId?: number;
  mode?: 'default' | 'clinic-slots';
}

export const CreateGroup: React.FC<CreateGroupProps> = ({
  onBack,
  onSuccess,
  duplicateGroupId,
  editGroupId,
  mode = 'default'
}) => {
  // Check URL parameters for edit mode and clinic slots mode
  const urlParams = new URLSearchParams(window.location.search);
  const urlEditGroupId = urlParams.get('groupId');
  const urlMode = urlParams.get('mode');
  const actualEditGroupId = editGroupId || (urlEditGroupId ? parseInt(urlEditGroupId) : undefined);
  const actualMode = mode || (urlMode === 'clinic-slots' ? 'clinic-slots' : 'default');
  const isClinicSlotsMode = actualMode === 'clinic-slots';

  // Initialize state and operations hooks
  const { state, actions } = useCreateGroupState();
  const groupOperations = useManageGroup(state, actions, onSuccess);
  const filteringOperations = useGroupFiltering(state, actions);

  // Combine operations for backward compatibility
  const operations = {
    ...groupOperations,
    ...filteringOperations
  };
  const { enrollmentSpecialists, isLoading: isLoadingSpecialists, error: specialistsError } = useEnrollmentSpecialists();


  // Set initial mode states
  useEffect(() => {
    if (duplicateGroupId) {
      actions.setIsDuplicateMode(true);
    }
    if (actualEditGroupId) {
      actions.setIsEditMode(true);
    }
  }, [duplicateGroupId, actualEditGroupId, actions]);

  // Load initial data when user selects any create type (filters or emrs)
  // Skip if in duplicate mode as data will be loaded by loadGroupDetailsForDuplicate
  useEffect(() => {
    if ((state.createType === 'filters' || state.createType === 'emrs') && !state.isDuplicateMode) {
      operations.loadInitialData(); // This will load group types and clinics
    }
  }, [state.createType, state.isDuplicateMode]);

  // Load group details when duplicating/editing
  useEffect(() => {
    if (duplicateGroupId) {
      operations.loadGroupDetailsForDuplicate(duplicateGroupId);
    }
    if (actualEditGroupId) {
      operations.loadGroupDetailsForEdit(actualEditGroupId);
    }
  }, [duplicateGroupId, actualEditGroupId]);

  // Load ONLY facilities when clinic is selected
  // Skip if in duplicate mode as facilities will be loaded by loadGroupDetailsForDuplicate
  useEffect(() => {
    if (state.selectedClinic && state.createType === 'filters' && !state.isDuplicateMode && !state.isEditMode) {
      const clinicId = parseInt(state.selectedClinic);
      filteringOperations.loadFacilitiesAndProviders(clinicId);
      actions.setShowFilters(false);
      actions.setSelectedFacilities([]);
      actions.setSelectedProviders([]);
      actions.resetFilters();
    }
  }, [state.selectedClinic, state.createType, state.isDuplicateMode, state.isEditMode]);

  // Load providers ONLY when facilities are selected
  // Skip if in duplicate mode as providers will be loaded by loadGroupDetailsForDuplicate
  useEffect(() => {
    if (state.selectedFacilities.length > 0 && state.selectedClinic && !state.isDuplicateMode && !state.isEditMode) {
      filteringOperations.loadProvidersForFacilities(parseInt(state.selectedClinic), state.selectedFacilities);
    }
  }, [state.selectedFacilities, state.selectedClinic, state.isDuplicateMode, state.isEditMode]);

  // Remove automatic filter loading - user should click Apply button to trigger filtering

  // Prepare options for components
  const vpeOptions = enrollmentSpecialists.map(vpe => ({
    value: vpe.hbox_user_id,
    label: vpe.es_name
  }));


  const facilityOptions = state.facilities.map(facility => ({
    value: facility.facility_id,
    label: facility.facility_name
  }));

  const providerOptions = state.providers.map(provider => ({
    value: provider.provider_id,
    label: provider.provider_name
  }));

  return (
    <div className="min-h-screen bg-purple-50">
      {/* Header */}
      <CreateGroupHeader
        onBack={onBack}
        isEditMode={state.isEditMode}
        isDuplicateMode={state.isDuplicateMode}
      />

      {/* Main Content */}
      <main className="p-6 bg-gray-50 min-h-screen">
        {/* Edit Group Layout */}
        {state.isEditMode && (
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Group Form Section */}
            <GroupFormSection
              groupName={state.groupName}
              selectedGroupType={state.selectedGroupType}
              selectedVPEs={state.selectedVPEs}
              createType={state.createType}
              groupTypes={state.groupTypes}
              vpeOptions={vpeOptions}
              isDuplicateMode={state.isDuplicateMode}
              isEditMode={state.isEditMode}
              groupDetails={state.groupDetails as any}
              isLoadingSpecialists={isLoadingSpecialists}
              onGroupNameChange={actions.setGroupName}
              onGroupTypeChange={actions.setSelectedGroupType}
              onVPEChange={actions.setSelectedVPEs}
              onCreateTypeChange={actions.setCreateType}
              onVpeManuallyInteracted={() => actions.setVpeManuallyInteracted(true)}
            />

            {/* Group Details */}
            {state.groupDetails && (
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Group Details</h3>

                {/* Filter Name and Applied Filter */}
                <div className="bg-purple-50 rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm font-medium text-gray-700">Filter Name</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-700">Applied Filter</span>
                    </div>
                  </div>
                </div>

                {/* Clinic Name */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-medium text-gray-700">Clinic Name</span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-900">{state.groupDetails.clinic_name || 'N/A'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Number of Patients */}
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <label className="block text-lg font-medium text-gray-700 mb-4">
                Number of Patients: <span className="font-bold">{state.filteredPatientsCount}</span>
              </label>
            </div>

            {/* Patient Details Table */}
            <PatientTableSection
              patients={state.patients as any}
              selectedPatientsForGroup={state.selectedPatientsForGroup}
              selectAll={state.selectAll}
              currentPage={state.currentPage}
              totalPages={state.totalPages}
              patientsPerPage={state.patientsPerPage}
              showPatientList={true}
              isEditMode={true}
              onPatientSelection={operations.handlePatientSelection}
              onSelectAll={operations.handleSelectAll}
              onCurrentPageChange={actions.setCurrentPage}
              onPatientsPerPageChange={actions.setPatientsPerPage}
            />

            {/* Action Buttons */}
            <ActionButtonsSection
              isLoading={state.isLoading}
              isEditMode={state.isEditMode}
              isDuplicateMode={state.isDuplicateMode}
              isUserEdited={state.isUserEdited}
              createType={state.createType}
              selectedPatientsForGroup={state.selectedPatientsForGroup}
              selectedEmrPatients={state.selectedEmrPatients}
              onCreateGroup={operations.handleCreateGroup}
              onBack={onBack}
            />
          </div>
        )}

        {/* Original Create/Duplicate Group Layout */}
        {!state.isEditMode && (
          <Card className="max-w-6xl mx-auto bg-white relative min-h-[600px]">
            {/* Full page overlay loader during duplicate mode loading */}
            {state.isLoading && state.isDuplicateMode && (
              <div className="absolute inset-0 bg-white bg-opacity-95 flex items-center justify-center z-50 rounded-lg">
                <div className="text-center">
                  <LoaderComponent size="lg" className="h-32" />
                  <p className="text-gray-600 text-lg font-medium mt-4">Copying and loading data...</p>
                  <p className="text-gray-500 text-sm mt-2">Please wait while we copy all filters and settings from the original group.</p>
                </div>
              </div>
            )}

            <CardHeader>
              <GroupFormSection
                groupName={state.groupName}
                selectedGroupType={state.selectedGroupType}
                selectedVPEs={state.selectedVPEs}
                createType={state.createType}
                groupTypes={state.groupTypes}
                vpeOptions={vpeOptions}
                isDuplicateMode={state.isDuplicateMode}
                isEditMode={state.isEditMode}
                groupDetails={state.groupDetails as any}
                isLoadingSpecialists={isLoadingSpecialists}
                onGroupNameChange={actions.setGroupName}
                onGroupTypeChange={actions.setSelectedGroupType}
                onVPEChange={actions.setSelectedVPEs}
                onCreateTypeChange={actions.setCreateType}
                onVpeManuallyInteracted={() => actions.setVpeManuallyInteracted(true)}
              />
            </CardHeader>

            <CardContent className="space-y-6">

              {/* Show form when type is selected */}
              {state.createType && (
                <>
                  {/* Clinic Selection */}
                  <ClinicSelectionSection
                    clinicSearchTerm={state.clinicSearchTerm}
                    showClinicDropdown={state.showClinicDropdown}
                    clinics={state.clinics as any}
                    selectedClinic={state.selectedClinic}
                    onClinicSearchChange={actions.setClinicSearchTerm}
                    onShowClinicDropdown={actions.setShowClinicDropdown}
                    onClinicSelect={operations.handleClinicSelect}
                  />

                  {/* Filter Section - Only for filters mode */}
                  {state.createType === 'filters' && (
                    <GroupFilteringComponent
                      // Filter state
                      selectedFacilities={state.selectedFacilities}
                      selectedProviders={state.selectedProviders}
                      selectedPrimaryDx={state.selectedPrimaryDx}
                      selectedSecondaryDx={state.selectedSecondaryDx}
                      selectedProgramEligibility={state.selectedProgramEligibility}
                      selectedPrimaryInsurance={state.selectedPrimaryInsurance}
                      selectedSecondaryInsurance={state.selectedSecondaryInsurance}
                      selectedInsuranceType={state.selectedInsuranceType}
                      zipCodes={state.zipCodes}
                      lastVisitDate={state.lastVisitDate}
                      excludeAssignedGroups={state.excludeAssignedGroups}
                      showFilters={state.showFilters}
                      filteredPatientsCount={state.filteredPatientsCount}
                      isLoading={state.isLoading}
                      selectedClinic={state.selectedClinic}

                      // Options
                      facilityOptions={facilityOptions}
                      providerOptions={providerOptions}
                      primaryDxOptions={state.primaryDxOptions}
                      secondaryDxOptions={state.secondaryDxOptions}
                      primaryInsuranceOptions={state.primaryInsuranceOptions}
                      secondaryInsuranceOptions={state.secondaryInsuranceOptions}
                      insuranceTypeOptions={state.insuranceTypeOptions}

                      // Patient search state
                      patientSearchTerm={state.patientSearchTerm}
                      searchResults={state.searchResults}
                      selectedPatientForAdding={state.selectedPatientForAdding}
                      numberOfPatientsToShow={state.numberOfPatientsToShow}
                      isDuplicateMode={state.isDuplicateMode}
                      selectedPatients={state.selectedPatients}

                      // Patient list state
                      patients={state.patients}
                      selectedPatientsForGroup={state.selectedPatientsForGroup}
                      selectAll={state.selectAll}
                      currentPage={state.currentPage}
                      totalPages={state.totalPages}
                      patientsPerPage={state.patientsPerPage}
                      showPatientList={state.showPatientList}

                      // Event handlers
                      onFacilitiesChange={actions.setSelectedFacilities}
                      onProvidersChange={actions.setSelectedProviders}
                      onPrimaryDxChange={actions.setSelectedPrimaryDx}
                      onSecondaryDxChange={actions.setSelectedSecondaryDx}
                      onProgramEligibilityChange={actions.setSelectedProgramEligibility}
                      onPrimaryInsuranceChange={actions.setSelectedPrimaryInsurance}
                      onSecondaryInsuranceChange={actions.setSelectedSecondaryInsurance}
                      onInsuranceTypeChange={actions.setSelectedInsuranceType}
                      onZipCodesChange={actions.setZipCodes}
                      onLastVisitDateChange={actions.setLastVisitDate}
                      onExcludeAssignedGroupsChange={actions.setExcludeAssignedGroups}
                      onApplyFilters={operations.handleApplyFilters}

                      // Patient search handlers
                      onPatientSearchChange={(value) => {
                        actions.setPatientSearchTerm(value);
                        operations.handlePatientSearch(value);
                      }}
                      onSelectPatientFromDropdown={operations.handleSelectPatientFromDropdown}
                      onAddPatientFromSearch={operations.handleAddPatientFromSearch}
                      onNumberOfPatientsChange={actions.setNumberOfPatientsToShow}
                      onShowPatientList={operations.handleShowPatientList}

                      // Patient selection handlers
                      onPatientSelection={operations.handlePatientSelection}
                      onSelectAll={operations.handleSelectAll}
                      onCurrentPageChange={actions.setCurrentPage}
                      onPatientsPerPageChange={actions.setPatientsPerPage}
                    />
                  )}

                  {/* EMR Input Section - Only for EMRs mode */}
                  {state.createType === 'emrs' && (
                    <EMRInputSection
                      patientEmrs={state.patientEmrs}
                      showEmrPatientList={state.showEmrPatientList}
                      emrPatients={state.emrPatients as any}
                      selectedEmrPatients={state.selectedEmrPatients}
                      emrSelectAll={state.emrSelectAll}
                      currentPage={state.currentPage}
                      patientsPerPage={state.patientsPerPage}
                      selectedClinic={state.selectedClinic}
                      isLoading={state.isLoading}
                      onPatientEmrsChange={actions.setPatientEmrs}
                      onPasteEmrs={operations.handlePasteEmrs}
                      onShowEmrPatientList={operations.handleShowEmrPatientList}
                      onEmrPatientSelection={operations.handleEmrPatientSelection}
                      onEmrSelectAll={operations.handleEmrSelectAll}
                      onCurrentPageChange={actions.setCurrentPage}
                      onPatientsPerPageChange={actions.setPatientsPerPage}
                    />
                  )}

                  {/* Action Buttons */}
                  <ActionButtonsSection
                    isLoading={state.isLoading}
                    isEditMode={state.isEditMode}
                    isDuplicateMode={state.isDuplicateMode}
                    isUserEdited={state.isUserEdited}
                    createType={state.createType}
                    selectedPatientsForGroup={state.selectedPatientsForGroup}
                    selectedEmrPatients={state.selectedEmrPatients}
                    onCreateGroup={operations.handleCreateGroup}
                    onBack={onBack}
                  />
                </>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default CreateGroup;